import changeCase from 'change-case'
import uniqueId from 'lodash/uniqueId'
import { useRef, useMemo } from 'react'
import { connect, MapStateToProps } from 'react-redux'
import {
  CheckBox,
  kuiperClassNames,
  renderIcon,
  Select,
  Tooltip,
} from '@opallabs/kuiper'
import { AnalyticsLayer } from '@opallabs/quanta'
import { selectors } from '@opallabs/wormhole'
import { Moment, State } from '@opallabs/wormhole/types'
import './styles.scss'

type PermissionMode = 'none' | 'additive' | 'replace'

const PERMISSION_MODE_OPTIONS = [
  { id: 'none', title: "Don't apply permissions" },
  { id: 'additive', title: 'Add to existing permissions' },
  { id: 'replace', title: 'Replace existing permissions' },
] as const satisfies { id: PermissionMode; title: string }[]

type StampCreatorProps = {
  limitedObjectName: string
  limiterSet: boolean
  objectName: 'moment' | 'content'
  onChange: (value: boolean) => void
  onChangeLimited: (value: boolean) => void
  onChangePermissionMode?: (value: PermissionMode) => void
  permissionMode?: PermissionMode
  theme: 'light' | 'dark'
  value: boolean | null
  valueLimited: boolean
  moment?: Moment
}

const renderTooltipText = (object: 'moment' | 'content') => {
  const objectString =
    {
      moment: 'content, notes, and labels',
      content: 'copy, assets, workflow, note, and labels',
    }[object] || 'details'

  return (
    `A Stamp is a template of your ${object} that anyone can quickly use. All ` +
    `${objectString} will be included. ${object === 'moment' ? 'Moments that have been added to a plan cannot be stamps.' : ''}`
  )
}

export const StampCreator: React.FC<StampCreatorProps> = ({
  limitedObjectName,
  limiterSet,
  objectName,
  onChange,
  onChangeLimited,
  onChangePermissionMode,
  permissionMode,
  theme,
  value,
  valueLimited,
  moment,
}) => {
  const tooltipId = useRef(uniqueId('stamp-creator'))
  const hasPairedBlock = Boolean(moment?.relationships.paired_block)
  const tooltipText = useMemo(() => renderTooltipText(objectName), [objectName])

  const selectedPermissionMode = PERMISSION_MODE_OPTIONS.find(
    (opt) => opt.id === (permissionMode ?? 'none')
  )

  return (
    <AnalyticsLayer
      name={`${changeCase.upperCaseFirst(objectName)} - Stamp Creator`}
    >
      <div className={kuiperClassNames('stamp-creator')}>
        <CheckBox
          ariaDescribedBy={tooltipId.current}
          theme={theme}
          disabled={hasPairedBlock}
          label={`This ${changeCase.upperCaseFirst(objectName)} is a Stamp`}
          value={value ?? false}
          onChange={onChange}
        />
        <Tooltip
          className={kuiperClassNames(
            'background-hoverable-info',
            'stamp-tooltip',
            objectName
          )}
          trigger={renderIcon('info', {
            style: { height: 14, width: 14 },
            ariaMode: 'hidden',
          })}
          content={tooltipText}
          inline
          position="top"
        />
        <span
          aria-hidden={true}
          className={kuiperClassNames('tooltip-text')}
          id={tooltipId.current}
        >
          {tooltipText}
        </span>
        {Boolean(value) && typeof onChangePermissionMode === 'function' && (
          <>
            <div
              className={kuiperClassNames(
                'select-wrapper',
                'stamp-permission-mode'
              )}
            >
              <span className={kuiperClassNames('stamp-permission-label')}>
                Permissions When Applied
              </span>
              <Select
                onSelect={(option) => onChangePermissionMode(option.id)}
                options={PERMISSION_MODE_OPTIONS}
                searchable={false}
                showDeselectIcon={false}
                theme={theme}
                value={selectedPermissionMode}
              />
            </div>
            <CheckBox
              disabled={!limiterSet}
              theme={theme}
              label={`Limit Stamp to this ${changeCase.upperCaseFirst(
                limitedObjectName
              )}`}
              value={valueLimited}
              onChange={onChangeLimited}
            />
          </>
        )}
      </div>
    </AnalyticsLayer>
  )
}

type StateProps = {
  moment?: Moment
}

type OwnProps = {
  momentId?: string
}

const mapStateToProps: MapStateToProps<StateProps, OwnProps, State> = (
  state,
  ownProps
) => {
  return {
    moment: ownProps.momentId
      ? selectors.selectMomentById(state, ownProps.momentId)
      : undefined,
  }
}

export const ConnectedStampCreator = connect(mapStateToProps)(StampCreator)
